import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 音楽プレーヤー風の音声プレーヤーコンポーネント
 * 機能: 再生/一時停止、進捗バー、シーク、時間表示
 */
const AudioPlayer = ({ audioUrl, onEnded }) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // 音声メタデータ読み込み完了
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    // 再生時間更新
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    // 再生終了
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (onEnded) onEnded();
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [onEnded]);

  // 再生/一時停止トグル
  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  // 進捗バーをクリックしてシーク
  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;

    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // 時間をフォーマット (MM:SS)
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // 進捗率計算
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="audio-player rounded-lg p-3 mt-2 shadow-sm" style={{ background: 'var(--primary-bg)' }}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* コントロールと進捗バー */}
      <div className="flex items-center gap-3">
        {/* 再生/一時停止ボタン */}
        <button
          onClick={togglePlayPause}
          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
          style={{ background: 'var(--primary)', color: 'var(--text-on-primary)' }}
          aria-label={isPlaying ? t('audio.pause') : t('audio.play')}
        >
          {isPlaying ? (
            // 一時停止アイコン
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          ) : (
            // 再生アイコン
            <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        {/* 進捗バーと時間表示 */}
        <div className="flex-1">
          {/* 進捗バー */}
          <div
            className="relative h-2 rounded-full cursor-pointer transition-colors"
            style={{ background: 'var(--border)' }}
            onClick={handleSeek}
          >
            {/* 進捗 */}
            <div
              className="absolute h-full rounded-full transition-all"
              style={{ width: `${progress}%`, background: 'var(--primary)' }}
            >
              {/* つまみ */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 shadow-md" style={{ background: 'var(--primary-strong)', borderColor: 'var(--text-on-primary)' }}></div>
            </div>
          </div>

          {/* 時間表示 */}
          <div className="flex justify-between mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
